import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import {
  debounce,
  isDesktopBreakpoint,
  mediaQueryLarge,
  requestYieldCallback,
} from '@theme/utilities';
import {
  ThemeEvents,
  VariantSelectedEvent,
  VariantUpdateEvent,
  SlideshowSelectEvent,
} from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Product Card
 *
 * Ziel dieser Fassung:
 *
 * - bestehende Shopify-Theme-Funktion erhalten
 * - keine aggressive globale Bild-Priorisierung
 * - keine unnötigen eager Requests
 * - keine Konkurrenz mit Checkout, Cart oder Navigation
 * - nächstes Hoverbild trotzdem rechtzeitig vorbereiten
 * - Event Listener sauber entfernen
 * - Varianten, Swatches und Quick Add vollständig erhalten
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink
 * @property {HTMLAnchorElement} [productTitleLink]
 * @property {HTMLAnchorElement} [cardGalleryLink]
 * @property {import('slideshow').Slideshow} [slideshow]
 * @property {import('quick-add').QuickAddComponent} [quickAdd]
 * @property {HTMLElement} [cardGallery]
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  /**
   * Speichert den zuletzt manuell gewählten Slide.
   *
   * @type {number | null}
   */
  #previousSlideIndex = null;

  /**
   * Verhindert parallele Preload-Vorgänge
   * desselben Vorschaubildes.
   *
   * @type {WeakSet<HTMLImageElement>}
   */
  #preparedImages = new WeakSet();

  /**
   * Timer für verzögertes Vorladen.
   *
   * @type {number | null}
   */
  #previewPreloadTimer = null;

  /**
   * Gibt die Produktseiten-URL zurück.
   */
  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gibt die aktuell ausgewählte Variant-ID zurück.
   *
   * @returns {string | null}
   */
  getSelectedVariantId() {
    const checkedInput =
      /** @type {HTMLInputElement | null} */ (
        this.querySelector(
          'input[type="radio"]:checked[data-variant-id]'
        )
      );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gibt den Hauptlink der Produktkarte zurück.
   *
   * @returns {HTMLAnchorElement | null}
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  /**
   * Gibt alle Varianteninputs zurück.
   *
   * @returns {NodeListOf<HTMLInputElement>}
   */
  get allVariants() {
    return this.querySelectorAll(
      'input[data-variant-id]'
    );
  }

  /**
   * Gibt den Swatch Variant Picker zurück.
   *
   * @returns {VariantPicker | null}
   */
  get variantPicker() {
    return this.querySelector(
      'swatches-variant-picker-component'
    );
  }

  /**
   * Quick-Add-Inhalte erst bei echter
   * Nutzerinteraktion vorbereiten.
   */
  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(
      this.productPageUrl
    );
  };

  /**
   * Navigation zur Produktseite.
   *
   * Modifier Keys bleiben erhalten.
   *
   * @param {Event} event
   * @param {URL} url
   */
  #navigateToURL = (event, url) => {
    const shouldOpenInNewTab =
      event instanceof MouseEvent &&
      (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.button === 1
      );

    if (shouldOpenInNewTab) {
      event.preventDefault();

      window.open(
        url.href,
        '_blank',
        'noopener'
      );

      return;
    }

    window.location.href = url.href;
  };

  /**
   * Wird aufgerufen, wenn die Produktkarte
   * in den DOM eingefügt wurde.
   */
  connectedCallback() {
    super.connectedCallback();

    const link =
      this.refs.productCardLink;

    if (
      !(link instanceof HTMLAnchorElement)
    ) {
      throw new Error(
        'Product card link not found'
      );
    }

    /*
     * Quick Add nur auf Desktop
     * via Hover/Fokus vorladen.
     */
    this.#handleQuickAdd();

    /*
     * Variant Events.
     */
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate
    );

    this.addEventListener(
      ThemeEvents.variantSelected,
      this.#handleVariantSelected
    );

    /*
     * Slideshow Auswahl.
     */
    this.addEventListener(
      SlideshowSelectEvent.eventName,
      this.#handleSlideshowSelect
    );

    /*
     * Responsive Quick-Add Verhalten.
     */
    mediaQueryLarge.addEventListener(
      'change',
      this.#handleQuickAdd
    );

    /*
     * Karten-Navigation.
     */
    this.addEventListener(
      'click',
      this.navigateToProduct
    );

    /*
     * WICHTIG:
     *
     * Nicht sämtliche Bilder der Produktkarte
     * sofort eager laden.
     *
     * Nur bei verschachtelten Hover-Galerien
     * das nächste Bild mit niedriger Priorität
     * vorbereiten.
     */
    this.#scheduleNextPreviewPreload();
  }

  /**
   * Sauberes Entfernen aller Listener.
   *
   * Besonders wichtig bei:
   *
   * - Collection AJAX
   * - Filter
   * - Pagination
   * - Theme Editor
   * - dynamisch neu gerenderten Karten
   */
  disconnectedCallback() {
    this.removeEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate
    );

    this.removeEventListener(
      ThemeEvents.variantSelected,
      this.#handleVariantSelected
    );

    this.removeEventListener(
      SlideshowSelectEvent.eventName,
      this.#handleSlideshowSelect
    );

    this.removeEventListener(
      'click',
      this.navigateToProduct
    );

    this.removeEventListener(
      'pointerenter',
      this.#fetchProductPageHandler
    );

    this.removeEventListener(
      'focusin',
      this.#fetchProductPageHandler
    );

    mediaQueryLarge.removeEventListener(
      'change',
      this.#handleQuickAdd
    );

    if (
      this.#previewPreloadTimer !== null
    ) {
      window.clearTimeout(
        this.#previewPreloadTimer
      );

      this.#previewPreloadTimer = null;
    }

    super.disconnectedCallback();
  }

  /**
   * Plant das Vorladen des nächsten
   * Vorschaubildes ein.
   *
   * Nicht sofort beim DOM-Aufbau, damit:
   *
   * - erstes Produktbild Vorrang hat
   * - Fonts nicht blockiert werden
   * - Header nicht blockiert wird
   * - Checkout-/Cart-Code nicht konkurriert
   */
  #scheduleNextPreviewPreload() {
    if (
      !this.refs.slideshow?.isNested
    ) {
      return;
    }

    if (
      this.#previewPreloadTimer !== null
    ) {
      window.clearTimeout(
        this.#previewPreloadTimer
      );
    }

    this.#previewPreloadTimer =
      window.setTimeout(() => {
        this.#previewPreloadTimer = null;

        this.#preloadNextPreviewImage();
      }, 120);
  }

  /**
   * Bereitet genau EIN nachfolgendes
   * Slideshow-Bild vor.
   *
   * Keine globale eager-Umstellung.
   */
  #preloadNextPreviewImage() {
    const slideshow =
      this.refs.slideshow;

    if (!slideshow) return;

    const currentSlide =
      slideshow.slides?.[
        slideshow.current
      ];

    if (
      !(currentSlide instanceof Element)
    ) {
      return;
    }

    const nextSlide =
      currentSlide.nextElementSibling;

    if (
      !(nextSlide instanceof Element)
    ) {
      return;
    }

    const image =
      nextSlide.querySelector('img');

    if (
      !(image instanceof HTMLImageElement)
    ) {
      return;
    }

    if (
      this.#preparedImages.has(image)
    ) {
      return;
    }

    this.#preparedImages.add(image);

    /*
     * Nur Lazy-Attribut dieses EINEN
     * kommenden Bildes entfernen.
     */
    if (
      image.getAttribute('loading') ===
      'lazy'
    ) {
      image.removeAttribute(
        'loading'
      );
    }

    /*
     * Niedrige Priorität:
     *
     * Das Hauptbild und kritische
     * Store-Ressourcen bleiben wichtiger.
     */
    try {
      image.fetchPriority = 'low';
    } catch {
      image.setAttribute(
        'fetchpriority',
        'low'
      );
    }

    /*
     * Async Decode verhindert möglichst,
     * dass Dekodierung Rendering blockiert.
     */
    try {
      image.decoding = 'async';
    } catch {
      // Kein Problem bei älteren Browsern.
    }
  }

  /**
   * Quick Add Events.
   */
  #handleQuickAdd = () => {
    this.removeEventListener(
      'pointerenter',
      this.#fetchProductPageHandler
    );

    this.removeEventListener(
      'focusin',
      this.#fetchProductPageHandler
    );

    if (
      isDesktopBreakpoint()
    ) {
      this.addEventListener(
        'pointerenter',
        this.#fetchProductPageHandler,
        {
          passive: true
        }
      );

      this.addEventListener(
        'focusin',
        this.#fetchProductPageHandler
      );
    }
  };

  /**
   * Varianten-Auswahl.
   *
   * @param {VariantSelectedEvent} event
   */
  #handleVariantSelected = (event) => {
    if (
      event.target !==
      this.variantPicker
    ) {
      this.variantPicker
        ?.updateSelectedOption(
          event.detail.resource.id
        );
    }
  };

  /**
   * Variante wurde geändert.
   *
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    /*
     * Variantenänderung einer Karte
     * darf nicht die gesamte Section
     * beeinflussen.
     */
    event.stopPropagation();

    this.updatePrice(event);

    this.#isUnavailableVariantSelected(
      event
    );

    this.#updateProductUrl(
      event
    );

    /*
     * Quick Add wird nach Variantenwechsel
     * aktualisiert.
     */
    this.refs.quickAdd
      ?.fetchProductPage(
        this.productPageUrl
      );

    if (
      event.target !==
      this.variantPicker
    ) {
      this.variantPicker
        ?.updateVariantPicker(
          event.detail.data.html
        );
    }

    this.#updateVariantImages();

    this.#previousSlideIndex =
      null;

    this.removeAttribute(
      'data-no-swatch-selected'
    );

    this.#updateOverflowList();
  };

  /**
   * Swatch Overflow neu berechnen.
   */
  #updateOverflowList() {
    const overflowList =
      this.querySelector(
        'swatches-variant-picker-component overflow-list'
      );

    const isActiveOverflowList =
      Boolean(
        overflowList?.querySelector(
          '[slot="overflow"]'
        )
      );

    if (
      !overflowList ||
      !isActiveOverflowList
    ) {
      return;
    }

    requestAnimationFrame(() => {
      /*
       * Nur ausführen, falls die Karte
       * weiterhin im Dokument existiert.
       */
      if (!this.isConnected) return;

      overflowList.dispatchEvent(
        new CustomEvent(
          'reflow',
          {
            bubbles: true,
            detail: {}
          }
        )
      );
    });
  }

  /**
   * Preis aktualisieren.
   *
   * @param {VariantUpdateEvent} event
   */
  updatePrice(event) {
    /*
     * Originale Theme-Struktur beibehalten.
     *
     * Es gibt im Card Rendering teilweise
     * zwei priceContainer.
     */
    const priceContainers =
      this.querySelectorAll(
        `product-price [ref='priceContainer']`
      );

    const priceContainer =
      priceContainers[1] ||
      priceContainers[0];

    const newPriceElement =
      event.detail.data.html
        ?.querySelector(
          `product-price [ref='priceContainer']`
        );

    if (
      newPriceElement &&
      priceContainer
    ) {
      morph(
        priceContainer,
        newPriceElement
      );
    }
  }

  /**
   * Produkt-URL bei Variantenauswahl
   * aktualisieren.
   *
   * @param {VariantUpdateEvent} event
   */
  #updateProductUrl(event) {
    const returnedHtml =
      event.detail.data.html;

    if (!returnedHtml) return;

    const anchorElement =
      returnedHtml.querySelector(
        'product-card a'
      );

    const featuredMediaUrl =
      returnedHtml
        .querySelector(
          'product-card-link'
        )
        ?.getAttribute(
          'data-featured-media-url'
        );

    const wrapper =
      this.closest(
        'product-card-link'
      );

    if (
      featuredMediaUrl &&
      wrapper
    ) {
      wrapper.setAttribute(
        'data-featured-media-url',
        featuredMediaUrl
      );
    }

    if (
      !(
        anchorElement instanceof
        HTMLAnchorElement
      )
    ) {
      return;
    }

    const href =
      anchorElement
        .getAttribute('href')
        ?.trim();

    /*
     * Leere URL nicht übernehmen.
     */
    if (!href) return;

    const productUrl =
      anchorElement.href;

    const {
      productCardLink,
      productTitleLink,
      cardGalleryLink
    } = this.refs;

    productCardLink.href =
      productUrl;

    if (
      cardGalleryLink instanceof
      HTMLAnchorElement
    ) {
      cardGalleryLink.href =
        productUrl;
    }

    if (
      productTitleLink instanceof
      HTMLAnchorElement
    ) {
      productTitleLink.href =
        productUrl;
    }
  }

  /**
   * Prüft Variantenverfügbarkeit.
   *
   * @param {VariantUpdateEvent} event
   */
  #isUnavailableVariantSelected(event) {
    const allVariants =
      /** @type {NodeListOf<HTMLInputElement>} */ (
        event.detail.data.html
          ?.querySelectorAll(
            'input:checked'
          ) || []
      );

    for (
      const variant of allVariants
    ) {
      this.#toggleAddToCartButton(
        variant.dataset.optionAvailable ===
          'true'
      );
    }
  }

  /**
   * Add-To-Cart Button aktivieren/deaktivieren.
   *
   * @param {boolean} enable
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton =
      this.querySelector(
        '.add-to-cart__button button'
      );

    if (
      addToCartButton instanceof
      HTMLButtonElement
    ) {
      addToCartButton.disabled =
        !enable;
    }
  }

  /**
   * Variantenbilder umschalten.
   *
   * Hier wird absichtlich KEIN
   * aggressives eager loading durchgeführt.
   */
  #updateVariantImages() {
    const {
      slideshow
    } = this.refs;

    const selectedOption =
      this.variantPicker
        ?.selectedOption;

    if (!selectedOption) {
      return;
    }

    const selectedImageId =
      selectedOption.dataset
        .optionMediaId;

    if (
      !slideshow ||
      !selectedImageId
    ) {
      return;
    }

    const {
      slides = []
    } = slideshow.refs;

    for (
      const slide of slides
    ) {
      if (
        slide.getAttribute(
          'variant-image'
        ) == null
      ) {
        continue;
      }

      slide.hidden =
        slide.getAttribute(
          'slide-id'
        ) !== selectedImageId;
    }

    slideshow.select(
      {
        id: selectedImageId
      },
      undefined,
      {
        animate: false
      }
    );

    /*
     * Erst NACH Auswahl lediglich
     * das kommende Bild vorbereiten.
     */
    this.#scheduleNextPreviewPreload();
  }

  /**
   * Slideshow Auswahl speichern.
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (
      event.detail.userInitiated
    ) {
      this.#previousSlideIndex =
        event.detail.index;
    }
  };

  /**
   * Varianten-Vorschau.
   *
   * @param {string} id
   */
  previewVariant(id) {
    const {
      slideshow
    } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    /*
     * Kein Await / kein Blockieren.
     *
     * Die bestehende Slideshow darf
     * unmittelbar reagieren.
     */
    slideshow.select(
      {
        id
      },
      undefined,
      {
        animate: false
      }
    );

    this.#scheduleNextPreviewPreload();
  }

  /**
   * Hover-Vorschaubild.
   *
   * @param {PointerEvent} event
   */
  previewImage(event) {
    /*
     * Nur echte Maus.
     *
     * Kein künstliches Hover-Verhalten
     * auf Touch-Geräten.
     */
    if (
      event.pointerType !== 'mouse'
    ) {
      return;
    }

    const {
      slideshow
    } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (
      this.#previousSlideIndex != null &&
      this.#previousSlideIndex > 0
    ) {
      slideshow.select(
        this.#previousSlideIndex,
        undefined,
        {
          animate: false
        }
      );

      this.#scheduleNextPreviewPreload();

      return;
    }

    /*
     * Nächstes Bild wurde nach Möglichkeit
     * bereits vorbereitet.
     *
     * Wechsel darf NICHT auf einen
     * Netzwerkrequest warten.
     */
    slideshow.next(
      undefined,
      {
        animate: false
      }
    );

    /*
     * Nun wiederum das folgende
     * Bild vorbereiten.
     */
    this.#scheduleNextPreviewPreload();
  }

  /**
   * Bild zurücksetzen.
   *
   * @param {PointerEvent} event
   */
  resetImage(event) {
    if (
      event.pointerType !== 'mouse'
    ) {
      return;
    }

    const {
      slideshow
    } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;

      slideshow.previous(
        undefined,
        {
          animate: false
        }
      );

      return;
    }

    this.#resetVariant();
  }

  /**
   * Variantenbild zurücksetzen.
   */
  #resetVariant = () => {
    const {
      slideshow
    } = this.refs;

    if (!slideshow) return;

    /*
     * Ausgewählte Variante besitzt Priorität.
     */
    if (
      this.variantPicker
        ?.selectedOption
    ) {
      const id =
        this.variantPicker
          .selectedOption
          .dataset
          .optionMediaId;

      if (id) {
        slideshow.select(
          {
            id
          },
          undefined,
          {
            animate: false
          }
        );

        return;
      }
    }

    /*
     * Ohne Variante initialen Slide verwenden.
     */
    const initialSlide =
      slideshow.initialSlide;

    const slideId =
      initialSlide?.getAttribute(
        'slide-id'
      );

    if (
      initialSlide &&
      slideshow.slides?.includes(
        initialSlide
      ) &&
      slideId
    ) {
      slideshow.select(
        {
          id: slideId
        },
        undefined,
        {
          animate: false
        }
      );

      return;
    }

    /*
     * Sicherer Fallback.
     */
    slideshow.previous(
      undefined,
      {
        animate: false
      }
    );
  };

  /**
   * Produktkartennavigation.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    /*
     * Theme Editor:
     *
     * Navigation bei ausgewählter
     * Karte deaktivieren.
     */
    if (
      this.hasAttribute(
        'data-no-navigation'
      )
    ) {
      return;
    }

    /*
     * Interaktive Elemente dürfen
     * keine Produktnavigation auslösen.
     */
    const interactiveElement =
      event.target.closest(
        [
          'button',
          'input',
          'label',
          'select',
          'textarea',
          'summary',
          '[role="button"]',
          '[tabindex="0"]',
          '[tabindex="1"]'
        ].join(', ')
      );

    if (interactiveElement) {
      return;
    }

    const link =
      this.refs.productCardLink;

    if (!link?.href) {
      return;
    }

    let linkURL;

    try {
      linkURL =
        new URL(
          link.href,
          window.location.origin
        );
    } catch {
      return;
    }

    const productCardAnchor =
      link.getAttribute('id');

    /*
     * Ohne Card Anchor einfach
     * normales Linkverhalten benutzen.
     */
    if (!productCardAnchor) {
      const nativeTarget =
        event.target.closest('a');

      if (!nativeTarget) {
        this.#navigateToURL(
          event,
          linkURL
        );
      }

      return;
    }

    const currentUrl =
      new URL(
        window.location.href
      );

    const parent =
      this.closest('li');

    currentUrl.hash =
      productCardAnchor;

    if (
      parent?.dataset.page
    ) {
      currentUrl.searchParams.set(
        'page',
        parent.dataset.page
      );
    }

    /*
     * Im Theme Editor keine
     * History-Manipulation.
     */
    if (
      !window.Shopify?.designMode
    ) {
      requestYieldCallback(() => {
        try {
          history.replaceState(
            {},
            '',
            currentUrl.toString()
          );
        } catch {
          /*
           * History-Fehler dürfen
           * Navigation niemals blockieren.
           */
        }
      });
    }

    /*
     * Bei echtem <a>-Klick übernimmt
     * der Browser selbst.
     */
    const targetLink =
      event.target.closest('a');

    if (!targetLink) {
      this.#navigateToURL(
        event,
        linkURL
      );
    }
  };

  /**
   * Reset leicht entprellen.
   */
  resetVariant =
    debounce(
      this.#resetVariant,
      100
    );
}

/**
 * ProductCard registrieren.
 */
if (
  !customElements.get(
    'product-card'
  )
) {
  customElements.define(
    'product-card',
    ProductCard
  );
}

/**
 * ============================================================
 * SWATCH VARIANT PICKER
 * ============================================================
 *
 * Bestehende VariantPicker-Funktion bleibt erhalten.
 */

/**
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Gebundener Eventhandler.
   *
   * Wichtig:
   *
   * Nicht jedes connectedCallback
   * ein neues bind() erzeugen,
   * das später nicht entfernt werden kann.
   */
  #boundVariantUrlUpdate =
    this.#handleCardVariantUrlUpdate.bind(
      this
    );

  /**
   * Verbindung.
   */
  connectedCallback() {
    super.connectedCallback();

    /*
     * Parent Card cachen.
     */
    this.parentProductCard =
      this.closest(
        'product-card'
      );

    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#boundVariantUrlUpdate
    );
  }

  /**
   * Cleanup.
   */
  disconnectedCallback() {
    this.removeEventListener(
      ThemeEvents.variantUpdate,
      this.#boundVariantUrlUpdate
    );

    super.disconnectedCallback?.();
  }

  /**
   * Karten-URL nach Variantenauswahl aktualisieren.
   */
  #handleCardVariantUrlUpdate() {
    if (
      !this.pendingVariantId ||
      !(
        this.parentProductCard instanceof
        ProductCard
      )
    ) {
      return;
    }

    const link =
      this.parentProductCard
        .refs
        .productCardLink;

    if (
      !(
        link instanceof
        HTMLAnchorElement
      )
    ) {
      return;
    }

    let currentUrl;

    try {
      currentUrl =
        new URL(
          link.href,
          window.location.origin
        );
    } catch {
      this.pendingVariantId =
        null;

      return;
    }

    currentUrl.searchParams.set(
      'variant',
      this.pendingVariantId
    );

    link.href =
      currentUrl.toString();

    this.pendingVariantId =
      null;
  }

  /**
   * Variante geändert.
   *
   * @param {Event} event
   */
  variantChanged(event) {
    if (
      !(event.target instanceof HTMLElement)
    ) {
      return;
    }

    /*
     * Nur Swatch Inputs.
     */
    const isSwatchInput =
      event.target instanceof
        HTMLInputElement &&
      event.target.name?.includes(
        '-swatch'
      );

    const clickedSwatch =
      event.target;

    const availableCount =
      Number.parseInt(
        clickedSwatch
          .dataset
          .availableCount ||
          '0',
        10
      );

    const firstAvailableVariantId =
      clickedSwatch
        .dataset
        .firstAvailableOrFirstVariantId;

    /*
     * Spezielle Behandlung:
     *
     * Diese Swatch-Kombination ist selbst
     * nicht erhältlich, aber eine passende
     * verfügbare Variante existiert.
     */
    if (
      isSwatchInput &&
      Number.isFinite(
        availableCount
      ) &&
      availableCount > 0 &&
      firstAvailableVariantId
    ) {
      /*
       * Andere Card-Komponenten sollen
       * diesen Zwischenschritt nicht verarbeiten.
       */
      event.stopPropagation();

      /*
       * Optische Auswahl sofort aktualisieren.
       */
      this.updateSelectedOption(
        clickedSwatch
      );

      /*
       * Basis-Produkt-URL.
       */
      const productUrl =
        this.dataset
          .productUrl
          ?.split('?')[0];

      if (!productUrl) {
        return;
      }

      let url;

      try {
        url =
          new URL(
            productUrl,
            window.location.origin
          );
      } catch {
        return;
      }

      /*
       * Erste verfügbare Variante laden.
       */
      url.searchParams.set(
        'variant',
        firstAvailableVariantId
      );

      /*
       * Shopify Section Rendering.
       */
      url.searchParams.set(
        'section_id',
        'section-rendering-product-card'
      );

      /*
       * Variant-ID für spätere URL
       * der bestehenden Karte merken.
       */
      this.pendingVariantId =
        firstAvailableVariantId;

      /*
       * Vorhandenen Theme-Fetch verwenden.
       */
      this.fetchUpdatedSection(
        url.href
      );

      return;
    }

    /*
     * Standardfälle bleiben vollständig
     * beim originalen VariantPicker.
     */
    super.variantChanged(event);
  }

  /**
   * Alle Swatches anzeigen.
   *
   * @param {Event} [event]
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const {
      overflowList
    } = this.refs;

    if (
      overflowList instanceof
      OverflowList
    ) {
      overflowList.showAll();
    }
  }
}

/**
 * SwatchesVariantPicker registrieren.
 */
if (
  !customElements.get(
    'swatches-variant-picker-component'
  )
) {
  customElements.define(
    'swatches-variant-picker-component',
    SwatchesVariantPickerComponent
  );
}